/**
 * Aggregate Net Exposure — Lab Aggregate list + Strix CM-style chrome.
 *
 * Live: `getBooksForBackendPreForGroup` then `getBooksForBackendForGroup`
 * (memberList scoped server-side). Demo: fixture books.
 * List always uses My PT (Lab Aggregate UI hides My PT / Total Book radios).
 * Detail Book / View Total still offer the book toggle (Lab workstation).
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Icon, Kpi, PageHead } from '@w7/ui'
import { useNetExposureBooks } from '@/api/hooks/net-exposure/useBooks'
import { useViewerCurrency } from '@/api/hooks/shared/useViewerCurrency'
import { count, money, moneyCompact } from '@/lib/format'
import {
  NE_SORTS,
  SPORTS,
  WORSE_THAN_STEPS,
  eventNames,
  filterEvents,
  summariseNetExposure,
  type BookType,
  type HorseAmount,
  type NeSort,
  type NetExposureEvent,
  type NetExposureMarket,
  type SportName,
} from './fixture'

/** Lab Aggregate Net Exposure list hardcodes this (toggle UI is commented out). */
const LIST_BOOK_TYPE: BookType = 'My PT'
import {
  advanceMarketIdsFromEvent,
  advanceWorkstationPath,
  workstationSearchFromMarket,
  type WorkstationMarketType,
} from './workstation-fixture'
import './net-exposure.css'

function toneClass(value: number): string {
  return value < 0 ? 'w7-ne-neg' : 'w7-ne-pos'
}

/** Lab list page: bare indianCurrency numbers — no ₹ / Rs. prefix. */
function Amt({ value }: { value: number }) {
  const currency = useViewerCurrency()
  return (
    <span className={`w7-ne-amt ${toneClass(value)}`}>
      {money(value, { round: true, symbol: false, currency })}
    </span>
  )
}

/** Market NE column is shown as `netExposure * -1` (Lab Backend-UI). */
function MarketNe({ value }: { value: number }) {
  return <Amt value={value * -1} />
}

function horseAt(horse: HorseAmount[], index: number): number | null {
  return horse[index]?.amount ?? null
}

function MatchOddsCells({ market }: { market: NetExposureMarket }) {
  const h0 = horseAt(market.horse, 0)
  const h1 = horseAt(market.horse, 1)
  const h2 = horseAt(market.horse, 2)
  const hasDraw = market.horse.length >= 3
  return (
    <>
      <td className="w7-ne-cell w7-ne-cell-ne">
        <MarketNe value={market.netExposure} />
      </td>
      <td className="w7-ne-cell">{h0 == null ? <span>0</span> : <Amt value={h0} />}</td>
      <td className="w7-ne-cell w7-ne-cell-x">
        {hasDraw && h2 != null ? <Amt value={h2} /> : <span className="w7-ne-dash">-</span>}
      </td>
      <td className="w7-ne-cell">{h1 == null ? <span>0</span> : <Amt value={h1} />}</td>
    </>
  )
}

function DetailBookLink({
  marketId,
  marketName,
  eventName,
  type,
  book,
}: {
  marketId: string
  marketName: string
  eventName: string
  type: WorkstationMarketType
  book: BookType
}) {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      className="w7-ne-ws"
      title="Detail Book"
      onClick={() =>
        navigate({
          pathname: `/book-management/net-exposure/workstation/${encodeURIComponent(marketId)}`,
          search: workstationSearchFromMarket({ marketName, eventName, type, book }),
        })
      }
    >
      <img src="/net-exposure/net_exposure.png" alt="" width={25} height={17} />
    </button>
  )
}

function EventBlock({ event, book }: { event: NetExposureEvent; book: BookType }) {
  const navigate = useNavigate()

  const fancyRows = event.data.filter((m) => !m.bookMakerforNetExposure && !m.isMatchOdds)
  const bookmakerRows = event.data.filter((m) => m.bookMakerforNetExposure)
  const pbMatchOdds = event.dataPrem.filter(
    (m) => m.bookmakerFlag && m.marketName === 'Match Odds (Bookmaker)',
  )
  const pbOtherBm = event.dataPrem.filter(
    (m) => m.bookmakerFlag && m.marketName !== 'Match Odds (Bookmaker)',
  )
  const pbFancy = event.dataPrem.filter((m) => !m.bookmakerFlag)
  const advanceIds = advanceMarketIdsFromEvent(event)
  const showViewTotal = advanceIds.length > 0

  const openViewTotal = () => {
    navigate({
      pathname: advanceWorkstationPath(advanceIds),
      search: new URLSearchParams({ event: event._id, book }).toString(),
    })
  }

  return (
    <table className="w7-ne-table">
      <tbody>
        <tr className="w7-ne-head">
          <th colSpan={2} className="w7-ne-event-title">
            <div className="w7-ne-event-title-inner">
              <span>{event._id}</span>
              {showViewTotal ? (
                <button
                  type="button"
                  className="w7-ne-view-total"
                  title="View Total — match odds + bookmakers"
                  onClick={openViewTotal}
                >
                  View Total
                </button>
              ) : null}
            </div>
          </th>
          <th className="w7-ne-col">Net Exposure</th>
          <th className="w7-ne-col">1</th>
          <th className="w7-ne-col">X</th>
          <th className="w7-ne-col">2</th>
          <th className="w7-ne-col-fancy">Fancy</th>
          <th className="w7-ne-col-icon" />
          <th className="w7-ne-col">Fancy Exposure</th>
        </tr>

        {event.matchOdds ? (
          <tr className="w7-ne-row">
            <td className="w7-ne-market">{event.matchOdds.marketName}</td>
            <td className="w7-ne-icon-cell">
              <DetailBookLink
                marketId={event.matchOdds.marketId}
                marketName={event.matchOdds.marketName}
                eventName={event._id}
                type="match"
                book={book}
              />
            </td>
            <MatchOddsCells market={event.matchOdds} />
            <td colSpan={3} />
          </tr>
        ) : null}

        {fancyRows.map((bookRow) => (
          <tr key={bookRow.marketId} className="w7-ne-row">
            <td colSpan={6} />
            <td className="w7-ne-market w7-ne-fancy-name">{bookRow.marketName}</td>
            <td className="w7-ne-icon-cell">
              <DetailBookLink
                marketId={bookRow.marketId}
                marketName={bookRow.marketName}
                eventName={event._id}
                type="fancy"
                book={book}
              />
            </td>
            <td className="w7-ne-cell w7-ne-cell-ne">
              <MarketNe value={bookRow.netExposure} />
            </td>
          </tr>
        ))}

        {bookmakerRows.map((bookRow) => (
          <tr key={bookRow.marketId} className="w7-ne-row">
            <td className="w7-ne-market">{bookRow.marketName}</td>
            <td className="w7-ne-icon-cell">
              <DetailBookLink
                marketId={bookRow.marketId}
                marketName={bookRow.marketName}
                eventName={event._id}
                type="bookmaker"
                book={book}
              />
            </td>
            <MatchOddsCells market={bookRow} />
            <td colSpan={3} />
          </tr>
        ))}

        {pbMatchOdds.map((bookRow) => (
          <tr key={bookRow.marketId} className="w7-ne-row">
            <td className="w7-ne-market">
              {bookRow.marketName === 'Multi Bet' ? 'Genie Combo Special' : bookRow.marketName} (PB)
            </td>
            <td className="w7-ne-icon-cell">
              <DetailBookLink
                marketId={bookRow.marketId}
                marketName={bookRow.marketName}
                eventName={event._id}
                type="bookmaker"
                book={book}
              />
            </td>
            <MatchOddsCells market={bookRow} />
            <td colSpan={3} />
          </tr>
        ))}

        {pbFancy.map((bookRow) => {
          const premiumFancy = bookRow.eventName === 'Premium Fancy'
          return (
            <tr key={bookRow.marketId} className="w7-ne-row">
              <td colSpan={6} />
              <td
                className={
                  premiumFancy ? 'w7-ne-market w7-ne-premium-fancy' : 'w7-ne-market w7-ne-fancy-name'
                }
              >
                {premiumFancy ? 'Premium Fancy' : `${bookRow.marketName} (PB)`}
              </td>
              <td className={`w7-ne-icon-cell${premiumFancy ? ' w7-ne-premium-fancy' : ''}`}>
                {premiumFancy ? null : (
                  <DetailBookLink
                    marketId={bookRow.marketId}
                    marketName={bookRow.marketName}
                    eventName={event._id}
                    type="fancy"
                    book={book}
                  />
                )}
              </td>
              <td
                className={`w7-ne-cell w7-ne-cell-ne${premiumFancy ? ' w7-ne-premium-fancy' : ''}`}
              >
                {premiumFancy ? (
                  <span className="w7-ne-premium-label">Premium Fancy Exposure</span>
                ) : (
                  <MarketNe value={bookRow.netExposure} />
                )}
              </td>
            </tr>
          )
        })}

        {pbOtherBm.map((bookRow) => (
          <tr key={bookRow.marketId} className="w7-ne-row">
            <td className="w7-ne-market">
              {bookRow.marketName === 'Multi Bet' ? 'Genie Combo Special' : bookRow.marketName} (PB)
            </td>
            <td className="w7-ne-icon-cell">
              <DetailBookLink
                marketId={bookRow.marketId}
                marketName={bookRow.marketName}
                eventName={event._id}
                type="bookmaker"
                book={book}
              />
            </td>
            <MatchOddsCells market={bookRow} />
            <td colSpan={3} />
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function NetExposurePage() {
  const bookType = LIST_BOOK_TYPE
  const [sport, setSport] = useState<SportName>('Cricket')
  const [eventName, setEventName] = useState<string>('')
  const [liveOnly, setLiveOnly] = useState(false)
  const [worseThan, setWorseThan] = useState<number | null>(null)
  const [sort, setSort] = useState<NeSort>('worst')

  const { events, isLoading, isError, error, isFetching, refetch } = useNetExposureBooks({
    selectedType: bookType,
    eventType: sport,
    eventname: eventName || 'All',
  })

  const names = useMemo(() => eventNames(events), [events])
  const visible = useMemo(
    () => filterEvents(events, sport, eventName || null, liveOnly, worseThan, sort),
    [events, sport, eventName, liveOnly, worseThan, sort],
  )
  const totals = useMemo(() => summariseNetExposure(visible), [visible])
  const isFiltered = sport !== 'All' || Boolean(eventName) || liveOnly || worseThan != null

  const clearFilters = () => {
    setSport('All')
    setEventName('')
    setLiveOnly(false)
    setWorseThan(null)
  }

  const sortLabel = NE_SORTS.find((entry) => entry.id === sort)?.label ?? 'Worst case first'
  const currency = useViewerCurrency()
  const rupees = (n: number) => money(n, { currency, symbol: currency !== 'INR' })
  const rupeesRound = (n: number) =>
    money(n, { round: true, currency, symbol: currency !== 'INR' })
  const rupeesCompact = (n: number) =>
    currency === 'INR' ? moneyCompact(n, false) : money(n, { round: true, currency })

  return (
    <div className="w7-ne">
      <PageHead
        title={
          <span className="w7-ne-title">
            <Icon name="net-exposure" size={22} className="w7-ne-title-mark" />
            Net Exposure
          </span>
        }
        subtitle="Open positions for linked members in this Aggregate group."
        actions={
          <Button variant="secondary" icon="refresh" busy={isFetching} onClick={() => refetch()}>
            Refresh
          </Button>
        }
      />

      <div className="w7-ne-toolbar">
        <div className="w7-ne-bar">
          <div className="w7-ne-chips" role="group" aria-label="Sport">
            {SPORTS.map((item) => (
              <button
                key={item.value}
                type="button"
                className="w7-ne-chip"
                aria-pressed={sport === item.value}
                onClick={() => {
                  setSport(item.value)
                  setEventName('')
                }}
              >
                {item.name}
              </button>
            ))}
            <button
              type="button"
              className="w7-ne-chip"
              aria-pressed={liveOnly}
              onClick={() => {
                const next = !liveOnly
                if (next && eventName) {
                  const selected = events.find((e) => e._id === eventName)
                  if (selected && !selected.inPlay) setEventName('')
                }
                setLiveOnly(next)
              }}
            >
              In play only
            </button>
          </div>

          <span className="w7-spacer" />

          {isFiltered ? (
            <Button
              variant="ghost"
              icon="close"
              onClick={clearFilters}
              title="Clears sport, event, live and worse-than. Book and sort stay."
            >
              Clear
            </Button>
          ) : null}
        </div>

        <div className="w7-ne-bar w7-ne-bar-controls">
          <select
            className="w7-ne-select w7-ne-select-event"
            value={eventName}
            onChange={(event) => setEventName(event.target.value)}
            aria-label="Select or search event"
          >
            <option value="">All events</option>
            {names
              .filter((name) => {
                const match = events.find((e) => e._id === name)
                if (!match) return false
                if (sport !== 'All' && match.sport !== sport) return false
                if (liveOnly && !match.inPlay) return false
                return true
              })
              .map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
          </select>

          <select
            className="w7-ne-select"
            value={worseThan === null ? '' : String(worseThan)}
            onChange={(event) =>
              setWorseThan(event.target.value ? Number(event.target.value) : null)
            }
            aria-label="Show only positions worse than"
          >
            <option value="">Any worst case</option>
            {WORSE_THAN_STEPS.map((step) => (
              <option key={step} value={String(step)}>
                Worse than {rupeesRound(step)}
              </option>
            ))}
          </select>

          <select
            className="w7-ne-select"
            value={sort}
            onChange={(event) => setSort(event.target.value as NeSort)}
            aria-label="Sort events by"
          >
            {NE_SORTS.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>

        <p className="w7-ne-filter-meta" aria-live="polite">
          {count(totals.events)} {totals.events === 1 ? 'event' : 'events'} ·{' '}
          {count(totals.markets)} {totals.markets === 1 ? 'market' : 'markets'} ·{' '}
          {count(totals.fancyLines)} fancy · {sortLabel}
          {worseThan != null ? ` · worse than ${rupeesRound(worseThan)}` : ''}
          {liveOnly ? ' · in play' : ''}
        </p>
      </div>

      <div className="w7-grid w7-grid-5 w7-ne-kpis">
        <Kpi
          label="Worst case"
          value={rupeesCompact(totals.worstCase)}
          exact={rupees(totals.worstCase)}
          tone={totals.worstCase < 0 ? 'loss' : 'neutral'}
          accent
          meta={`Across ${count(totals.events)} ${totals.events === 1 ? 'event' : 'events'}`}
        />
        <Kpi
          label="Best case"
          value={rupeesCompact(totals.bestCase)}
          exact={rupees(totals.bestCase)}
          tone={totals.bestCase > 0 ? 'profit' : 'neutral'}
          meta="If every market lands the book's way"
        />
        <Kpi
          label="In play"
          value={rupeesCompact(totals.liveWorst)}
          exact={rupees(totals.liveWorst)}
          tone={totals.liveWorst < 0 ? 'loss' : 'neutral'}
          meta={`${count(totals.liveEvents)} of ${count(totals.events)} events are live`}
          onClick={() => setLiveOnly((value) => !value)}
        />
        <Kpi
          label="Markets"
          value={count(totals.markets)}
          meta={`In ${count(totals.events)} ${totals.events === 1 ? 'event' : 'events'}`}
        />
        <Kpi
          label="Fancy lines"
          value={count(totals.fancyLines)}
          meta="Session and premium fancy books in view"
        />
      </div>

      <div className="w7-ne-kpi-note">
        <p className="w7-card-sub">
          Worst case adds up the losing outcome of every market as though they all settled against
          the book at the same moment. Inside a single market the 1, X and 2 cells are alternatives —
          the row&rsquo;s worst is the smallest of them, never their sum.
        </p>
        <p className="w7-ne-basis-note" data-book-type={bookType}>
          Showing
          {sport !== 'All' ? (
            <>
              {' '}
              <strong>{sport}</strong>
            </>
          ) : (
            <>
              {' '}
              <strong>All sports</strong>
            </>
          )}
          {liveOnly ? (
            <>
              {' '}
              · <strong>In play only</strong>
            </>
          ) : null}
          {worseThan != null ? (
            <>
              {' '}
              · worse than <strong>{rupeesRound(worseThan)}</strong>
            </>
          ) : null}
        </p>
        {isFiltered ? (
          <p className="w7-card-sub w7-dimmer">
            These figures describe the {count(totals.events)}{' '}
            {totals.events === 1 ? 'event' : 'events'} in view, not your whole book.
          </p>
        ) : null}
      </div>

      {isLoading ? (
        <div className="w7-ne-empty">Loading open books…</div>
      ) : isError ? (
        <div className="w7-ne-empty">
          Could not load books
          {error instanceof Error && error.message ? `: ${error.message}` : ''}
        </div>
      ) : visible.length === 0 ? (
        <div className="w7-ne-empty">
          {isFiltered ? 'No events match these filters' : 'No Data Found'}
        </div>
      ) : (
        <div className="w7-ne-events">
          {visible.map((event) => (
            <EventBlock key={event._id} event={event} book={bookType} />
          ))}
        </div>
      )}
    </div>
  )
}
